import { addNotification } from "../store/slice/miscSlice";
import { baseURL, wsURL } from "../constant";
import { playNotificationChime } from "../app/notification/helpers";
import { logOutUser } from "../store/slice/utils";

export const NOTIFICATIONS_SOCKET_RECONNECTED_EVENT = "ordlnk:notifications-socket-reconnected";

export class WebSocketService {
  constructor() {
    this.socket = null;
    this.wsUrl = `${wsURL}/notifications/`;
    this.stopped = true;
    this.reconnectAttempts = 0;
    this.lifecycle = 0;
    this.connectionGen = 0;
    // Set when the socket drops unexpectedly; cleared after the next open resync.
    this.pendingFailureResync = false;
  }

  setStore(store) {
    this.store = store;
  }

  /**
   * True when a socket is already usable or in flight.
   *
   * @return {boolean}
   */
  isSocketActive() {
    if (!this.socket) {
      return false;
    }
    const state = this.socket.readyState;
    // Test doubles may omit readyState; a held socket still counts as active.
    if (state === undefined) {
      return true;
    }
    return state === WebSocket.OPEN || state === WebSocket.CONNECTING;
  }

  /**
   * Initializes the WebSocket connection and sets up event listeners.
   * Guarantees at most one live /notifications/ socket.
   */
  createWebSocket() {
    if (this.isSocketActive()) {
      return;
    }

    this.stopped = false;
    clearTimeout(this.reconnectTimer);

    // Drop a dead/closing socket without letting its close handler reconnect.
    if (this.socket) {
      this.detachSocket(this.socket);
      this.socket = null;
    }

    this.connectionGen += 1;
    const connectionGen = this.connectionGen;
    const socket = new WebSocket(this.wsUrl);
    this.socket = socket;

    const onClose = () => {
      if (this.connectionGen !== connectionGen || this.socket !== socket) {
        return;
      }
      this.handleClose();
    };
    const onError = () => {
      if (this.connectionGen !== connectionGen || this.socket !== socket) {
        return;
      }
      this.handleError();
    };
    const onOpen = () => {
      if (this.connectionGen !== connectionGen || this.socket !== socket) {
        return;
      }
      this.handleOpen();
    };
    const onMessage = event => {
      if (this.connectionGen !== connectionGen || this.socket !== socket) {
        return;
      }
      this.handleMessage(event);
    };

    this._boundClose = onClose;
    this._boundError = onError;
    this._boundOpen = onOpen;
    this._boundMessage = onMessage;

    socket.addEventListener("close", onClose);
    socket.addEventListener("error", onError);
    socket.addEventListener("message", onMessage);
    socket.addEventListener("open", onOpen);
  }

  handleOpen = () => {
    const shouldResyncInbox = this.pendingFailureResync;
    this.pendingFailureResync = false;
    this.reconnectAttempts = 0;
    this.store.dispatch({ type: "resourceSync/connected" });
    // Boot load covers first open; token-refresh reconnect is intentional.
    // Only an unexpected drop needs an inbox heal.
    if (shouldResyncInbox) {
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SOCKET_RECONNECTED_EVENT));
    }
  };

  /**
   * Route one inbound message on the shared notifications socket.
   *
   * @param {MessageEvent} event
   */
  handleMessage = event => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      return;
    }

    // Route by envelope ``type`` on the shared notifications socket.
    if (data.action && data.action === "refresh_token") {
      this.refreshToken();
    } else if (data.type === "resource.changed") {
      this.store.dispatch({ type: "resourceSync/event", payload: data });
    } else if (data.type === "data_reset") {
      this.store.dispatch({ type: "dataReset/event", payload: data });
    } else if (data.type) {
      // Unknown keyed event — never treat as a bell notification.
      return;
    } else {
      this.store.dispatch(addNotification(data));
      playNotificationChime();
    }
  };

  /**
   * Handles WebSocket closure by attempting to reconnect after a delay.
   */
  handleClose = () => {
    this.socket = null;
    if (!this.stopped && !this.refreshRequest) {
      // Unexpected drop (not closeWebSocket / token refresh) — heal inbox after reopen.
      this.pendingFailureResync = true;
      const delay = Math.min(1000 * 2 ** this.reconnectAttempts++, 30000) + Math.random() * 1000;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.createWebSocket(), delay);
    }
  };

  /**
   * Logs WebSocket errors and ensures the connection is closed.
   */
  handleError = () => {
    this.socket?.close();
  };

  /**
   * Closes the current WebSocket connection and removes event listeners.
   *
   * @param {{intentional: (boolean|undefined)}=} options intentional=true means
   *     logout / token refresh — do not treat the next open as a failure resync.
   */
  closeWebSocket({ intentional = true } = {}) {
    if (intentional) {
      this.pendingFailureResync = false;
    }
    this.stopped = true;
    this.lifecycle += 1;
    this.connectionGen += 1;
    this.refreshRequest = null;
    clearTimeout(this.reconnectTimer);
    if (this.socket) {
      const socket = this.socket;
      this.socket = null;
      this.detachSocket(socket);
    }
  }

  /**
   * Remove this service's listeners from a socket and close it.
   *
   * @param {WebSocket} socket
   */
  detachSocket(socket) {
    if (this._boundClose) socket.removeEventListener("close", this._boundClose);
    if (this._boundError) socket.removeEventListener("error", this._boundError);
    if (this._boundMessage) socket.removeEventListener("message", this._boundMessage);
    if (this._boundOpen) socket.removeEventListener("open", this._boundOpen);
    try {
      socket.close();
    } catch (error) {
      // Ignore teardown races.
    }
  }

  /**
   * Initiates a token refresh and attempts to reconnect the WebSocket on success.
   */
  refreshToken() {
    if (this.stopped || this.refreshRequest) return;
    const lifecycle = this.lifecycle;
    const request = fetch(`${baseURL}/api_user/refresh_token/`, {
      method: "GET",
      credentials: "include",
    })
      .then(response => {
        if (this.stopped || lifecycle !== this.lifecycle) return;
        if (response.ok) {
          if (!this.stopped) this.reconnectWebSocket();
        } else {
          this.store.dispatch(logOutUser());
        }
      })
      .catch(() => {
        if (!this.stopped && lifecycle === this.lifecycle) this.store.dispatch(logOutUser());
      })
      .finally(() => {
        if (this.refreshRequest === request) this.refreshRequest = null;
      });
    this.refreshRequest = request;
  }

  /**
   * Reconnects the WebSocket by first closing any existing connection.
   */
  reconnectWebSocket() {
    this.closeWebSocket({ intentional: true });
    this.createWebSocket();
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;
